import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { useProjectAuth } from './useProjectAuth'
import { getCached, setCached } from '../lib/queryCache'
import * as XLSX from 'xlsx'

const Toast = ({ message, type, onClose }) => (
  <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-2xl text-sm font-medium
    ${type === 'success' ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white'}`}>
    <span>{message}</span>
    <button onClick={onClose} className="ml-2 opacity-70 hover:opacity-100">×</button>
  </div>
)

const fmt  = (n) => n ? Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'
const fmtN = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

// ─── Tender Indirect Tab ───────────────────────────────────────────────────────
function TenderIndirectTab({
  items, loading, uploadPreview, uploading,
  onFileSelect, selected, onToggle, onToggleAll, onDeleteSelected,
  editItem, editForm, onEdit, onEditChange, onEditSave, onEditCancel,
  onSetUploadMode, can,
}) {
  const [collapsed, setCollapsed] = useState(new Set())
  const toggleCollapse = (desc) => setCollapsed(s => { const n = new Set(s); n.has(desc) ? n.delete(desc) : n.add(desc); return n })
  const headerRef = useRef(null)
  const bodyRef   = useRef(null)
  const footerRef = useRef(null)
  useEffect(() => {
    const body = bodyRef.current
    const header = headerRef.current
    const footer = footerRef.current
    if (!body) return
    const sync = () => {
      if (header) header.scrollLeft = body.scrollLeft
      if (footer) footer.scrollLeft = body.scrollLeft
    }
    body.addEventListener('scroll', sync)
    return () => body.removeEventListener('scroll', sync)
  }, [])

  const lineItems   = items.filter(i => !i.is_package_header)
  const totalBudget = lineItems.reduce((s, i) => s + (parseFloat(i.tender_budget) || 0), 0)

  const monthKeys = useMemo(() => {
    const keys = new Set()
    items.forEach(i => { if (i.monthly_data) Object.keys(i.monthly_data).forEach(k => keys.add(k)) })
    return Array.from(keys).sort()
  }, [items])

  const monthTotals = useMemo(() => {
    const totals = {}
    monthKeys.forEach(k => {
      totals[k] = items.filter(i => i.is_package_header && i.monthly_data).reduce((s,i) => s+(i.monthly_data[k]||0), 0)
    })
    return totals
  }, [items, monthKeys])

  const grandMonthTotal = Object.values(monthTotals).reduce((s,v) => s+v, 0)

  return (
    <div className="flex flex-col gap-4">

      {/* Upload zone */}
      <div className="bg-white rounded-xl border border-dashed border-[#E5E7EB] p-5 text-center">
        <input type="file" accept=".xlsx,.xls" id="indirect-upload" className="hidden"
          onChange={e => { if (e.target.files[0]) onFileSelect(e.target.files[0]); e.target.value = '' }} />
        <label htmlFor="indirect-upload" className="cursor-pointer flex flex-col items-center gap-2">
          <div className="w-9 h-9 rounded-full bg-[#F3F4F6] flex items-center justify-center text-lg">📤</div>
          <span className="text-sm text-[#6B7280]">Upload Indirect Cost Excel</span>
        </label>
      </div>

      {/* Stats */}
      {items.length > 0 && !uploadPreview && (
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-4">
            <div className="text-xl font-extrabold text-[#0F1017]">{lineItems.length}</div>
            <div className="text-xs text-[#6B7280] mt-1">Activities</div>
          </div>
          <div className="bg-[#0F1017] rounded-xl p-4">
            <div className="text-lg font-bold text-white">{fmtN(totalBudget)}</div>
            <div className="text-xs text-[#9CA3AF] mt-1">Total Budget (SAR)</div>
          </div>
          <div className="bg-[#1A7A4A] rounded-xl p-4">
            <div className="text-lg font-bold text-white">{fmtN(grandMonthTotal)}</div>
            <div className="text-xs text-white/70 mt-1">Total Monthly (SAR)</div>
          </div>
        </div>
      )}

      {/* Upload preview */}
      {uploadPreview && (
        <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E7EB]">
            <span className="text-sm font-semibold text-[#0F1017]">معاينة — {uploadPreview.length} بند</span>
            <div className="flex gap-2">
              <button onClick={() => onSetUploadMode('cancel')} className="px-3 py-1.5 text-xs border border-[#E5E7EB] rounded-lg hover:bg-[#FAFAFA]">Cancel</button>
              <button onClick={() => onSetUploadMode('asking')} disabled={uploading}
                className="px-3 py-1.5 text-xs bg-[#1A7A4A] text-white rounded-lg disabled:opacity-60 flex items-center gap-1.5">
                {uploading ? 'Uploading...' : '✓ Confirm Upload'}
              </button>
            </div>
          </div>
          <div className="overflow-x-auto max-h-64">
            <table className="w-full text-xs">
              <thead className="bg-[#F9FAFB]"><tr>
                <th className="px-3 py-2 text-left">No.</th>
                <th className="px-3 py-2 text-left">Description</th>
                {/* Qty/Unit/Rate are Indirect-only, conditional on the
                    parsed preview actually containing them -- legacy
                    files (no qty column) show the exact same 3-column
                    preview as before; new files with qty/unit/rate get
                    the extra columns. */}
                {uploadPreview.some(r => r.qty != null) && <th className="px-3 py-2 text-right">Qty</th>}
                {uploadPreview.some(r => r.unit) && <th className="px-3 py-2 text-left">Unit</th>}
                {uploadPreview.some(r => r.rate != null) && <th className="px-3 py-2 text-right">Rate</th>}
                <th className="px-3 py-2 text-right">Budget</th>
              </tr></thead>
              <tbody className="divide-y divide-[#F3F4F6]">
                {uploadPreview.slice(0,30).map((r,i) => (
                  <tr key={i} className={r.is_package_header ? 'bg-[#2E2C29]' : ''}>
                    <td className={`px-3 py-1.5 font-mono ${r.is_package_header?'text-amber-300':'text-[#9CA3AF]'}`}>{r.item_number||'—'}</td>
                    <td className={`px-3 py-1.5 ${r.is_package_header?'font-bold text-white':'text-[#2E2C29]'}`}>{r.description}</td>
                    {uploadPreview.some(x => x.qty != null) && <td className="px-3 py-1.5 text-right font-mono">{r.qty != null ? fmtN(r.qty) : '—'}</td>}
                    {uploadPreview.some(x => x.unit) && <td className="px-3 py-1.5">{r.unit || '—'}</td>}
                    {uploadPreview.some(x => x.rate != null) && <td className="px-3 py-1.5 text-right font-mono">{r.rate != null ? fmtN(r.rate) : '—'}</td>}
                    <td className="px-3 py-1.5 text-right font-mono">{fmtN(r.tender_budget)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Delete toolbar */}
      {!uploadPreview && selected.size > 0 && can('Budget.Edit') && (
        <button onClick={onDeleteSelected} className="self-start px-3 py-1.5 text-xs bg-red-50 border border-red-200 text-red-600 rounded-lg">
          🗑 Delete {selected.size}
        </button>
      )}

      {/* Edit Modal */}
      {editItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md">
            <h3 className="font-bold text-[#0F1017] mb-4">Edit Item</h3>
            <div className="space-y-3">
              {[{key:'description',label:'Description'},{key:'cost_code',label:'Cost Code'},{key:'department',label:'Department'},{key:'tender_budget',label:'Budget',type:'number'}].map(f => (
                <div key={f.key}>
                  <label className="text-xs text-[#6B7280] mb-1 block">{f.label}</label>
                  <input type={f.type||'text'} value={editForm[f.key]||''} onChange={e => onEditChange(f.key, e.target.value)}
                    className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none" />
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={onEditCancel} className="px-4 py-2 text-sm border border-[#E5E7EB] rounded-lg">Cancel</button>
              {can('Budget.Edit') && (
                <button onClick={onEditSave} className="px-4 py-2 text-sm bg-[#1A7A4A] text-white rounded-lg">Save</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main content */}
      {!uploadPreview && (
        loading ? <div className="text-center py-16 text-[#9CA3AF] text-sm">Loading...</div>
        : items.length === 0 ? <div className="text-center py-16 text-[#9CA3AF] text-sm">لا يوجد بيانات — ارفع Excel أولاً</div>
        : (
          <div className="flex gap-4 items-start">

            {/* Budget Table with collapse */}
            <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden flex-shrink-0" style={{width:440}}>
              <div className="overflow-auto" style={{maxHeight:'78vh'}}>
                <table className="w-full text-xs">
                  <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB] sticky top-0 z-10">
                    <tr>
                      <th className="px-2 py-2.5"><input type="checkbox" checked={selected.size===lineItems.length && lineItems.length>0} onChange={onToggleAll}/></th>
                      <th className="text-center px-2 py-2.5 text-xs font-semibold text-[#6B7280] uppercase">No.</th>
                      <th className="text-left px-3 py-2.5 text-xs font-semibold text-[#6B7280] uppercase">Description</th>
                      <th className="text-left px-2 py-2.5 text-xs font-semibold text-[#1A7A4A] uppercase">Code</th>
                      <th className="text-right px-3 py-2.5 text-xs font-semibold text-[#6B7280] uppercase">Budget</th>
                      <th className="px-2 py-2.5"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#F3F4F6]">
                    {(() => {
                      let currentPkg = null
                      return items.map((item, idx) => {
                        if (item.is_package_header) {
                          currentPkg = item.description
                          const isCol = collapsed.has(currentPkg)
                          return (
                            <tr key={item.id||idx} className="cursor-pointer" onClick={() => toggleCollapse(currentPkg)}>
                              <td colSpan={5} className="px-3 py-2 bg-[#2E2C29] hover:bg-[#3a3835]">
                                <div className="flex items-center justify-between">
                                  <span className="text-xs font-bold text-white">
                                    <span className="text-[#9CA3AF] mr-1">{isCol ? '▶' : '▼'}</span>
                                    {item.item_number && <span className="text-amber-300 mr-1">{item.item_number}</span>}
                                    {item.description}
                                  </span>
                                  <span className="text-xs font-mono text-amber-300">{fmtN(item.tender_budget)}</span>
                                </div>
                              </td>
                            </tr>
                          )
                        }
                        if (collapsed.has(currentPkg)) return null
                        const isChecked = selected.has(item.id)
                        return (
                          <tr key={item.id||idx} className={`hover:bg-[#FAFAFA] ${isChecked?'bg-blue-50':''}`}>
                            <td className="px-2 py-2 text-center"><input type="checkbox" checked={isChecked} onChange={() => onToggle(item.id)}/></td>
                            <td className="px-2 py-2 text-center font-mono text-xs text-[#9CA3AF]">{item.item_number||'—'}</td>
                            <td className="px-3 py-2 text-xs text-[#2E2C29] max-w-[140px] truncate" title={item.description}>{item.description}</td>
                            <td className="px-2 py-2 font-mono text-xs text-[#1A7A4A]">{item.cost_code||'—'}</td>
                            <td className="px-3 py-2 text-right font-bold font-mono text-xs">{fmtN(item.tender_budget)}</td>
                            <td className="px-2 py-2 text-center"><button onClick={()=>onEdit(item)} className="text-[#6B7280] hover:text-[#1A7A4A]">✏️</button></td>
                          </tr>
                        )
                      })
                    })()}
                  </tbody>
                  <tfoot className="bg-[#0F1017] sticky bottom-0">
                    <tr>
                      <td colSpan={3} className="px-3 py-2.5 text-xs font-bold text-slate-400 uppercase">Grand Total</td>
                      <td className="px-3 py-2.5 text-right text-sm font-bold text-white font-mono">{fmtN(totalBudget)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Monthly section */}
            {monthKeys.length > 0 && (
              <div className="flex flex-col gap-3 flex-1 min-w-0">

                {/* Monthly Table header with collapse all/expand all */}
                <div className="flex items-center justify-between">
                  <div className="text-sm font-bold text-[#0F1017]">Planned Monthly Distribution</div>
                  <div className="flex gap-2">
                    <button onClick={() => setCollapsed(new Set(items.filter(i=>i.is_package_header).map(i=>i.description)))}
                      className="px-3 py-1.5 text-xs border border-[#E5E7EB] rounded-lg hover:bg-[#FAFAFA] text-[#6B7280]">
                      ▶ Collapse All
                    </button>
                    <button onClick={() => setCollapsed(new Set())}
                      className="px-3 py-1.5 text-xs border border-[#E5E7EB] rounded-lg hover:bg-[#FAFAFA] text-[#6B7280]">
                      ▼ Expand All
                    </button>
                  </div>
                </div>

                {/* Monthly Table — fixed header/footer, scrollable body */}
                <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
                  {/* Fixed Header */}
                  <div ref={headerRef} style={{overflowX:'scroll', scrollbarWidth:'none'}} className="[&::-webkit-scrollbar]:hidden">
                    <table className="text-xs border-collapse" style={{minWidth: monthKeys.length*80+180, tableLayout:'fixed', width:'100%'}}>
                      <colgroup>
                        <col style={{width:170}}/>
                        {monthKeys.map(k => <col key={k} style={{width:80}}/>)}
                        <col style={{width:90}}/>
                      </colgroup>
                      <thead>
                        <tr className="bg-[#1A7A4A]">
                          <th className="text-left px-3 py-2.5 text-white font-bold text-xs sticky left-0 z-20 bg-[#1A7A4A]">Package</th>
                          {monthKeys.map(k => (
                            <th key={k} className="px-2 py-2.5 text-right text-white font-semibold text-xs whitespace-nowrap">
                              {new Date(k+'-01').toLocaleDateString('en-US',{month:'short',year:'2-digit'})}
                            </th>
                          ))}
                          <th className="px-3 py-2.5 text-right text-white font-bold text-xs sticky right-0 z-20 bg-[#15693f]">Total</th>
                        </tr>
                      </thead>
                    </table>
                  </div>
                  {/* Scrollable Body */}
                  <div ref={bodyRef} className="monthly-scroll-body" style={{overflowX:'auto', overflowY:'auto', maxHeight:'calc(78vh - 220px)'}}>
                    <table className="text-xs border-collapse" style={{minWidth: monthKeys.length*80+180, tableLayout:'fixed', width:'100%'}}>
                      <colgroup>
                        <col style={{width:170}}/>
                        {monthKeys.map(k => <col key={k} style={{width:80}}/>)}
                        <col style={{width:90}}/>
                      </colgroup>
                      <tbody className="divide-y divide-[#F3F4F6]">
                        {items.filter(i => i.is_package_header && i.monthly_data && Object.keys(i.monthly_data).length>0).map((item,idx) => {
                          const rowTotal = Object.values(item.monthly_data).reduce((s,v)=>s+v,0)
                          const bgCell = idx%2===0?'#fff':'#FAFAFA'
                          return (
                            <tr key={item.id||idx}>
                              <td style={{position:'sticky', left:0, zIndex:5, background:bgCell, borderRight:'1px solid #E5E7EB', maxWidth:170, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}
                                className="px-3 py-2 text-xs font-semibold text-[#0F1017]" title={item.description}>
                                {item.item_number && <span className="text-amber-500 font-mono mr-1">{item.item_number}</span>}
                                {item.description}
                              </td>
                              {monthKeys.map(k => (
                                <td key={k} className="px-2 py-2 text-right font-mono text-blue-700">
                                  {item.monthly_data[k] ? fmtN(item.monthly_data[k]) : <span className="text-[#E5E7EB]">—</span>}
                                </td>
                              ))}
                              <td style={{position:'sticky', right:0, zIndex:5, background: idx%2===0?'#F0FDF4':'#E8FAF0', borderLeft:'1px solid #E5E7EB'}}
                                className="px-3 py-2 text-right font-bold font-mono text-[#1A7A4A]">
                                {fmtN(rowTotal)}
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                  {/* Fixed Footer */}
                  <div ref={footerRef} style={{overflowX:'scroll', scrollbarWidth:'none'}} className="[&::-webkit-scrollbar]:hidden">
                    <table className="text-xs border-collapse" style={{minWidth: monthKeys.length*80+180, tableLayout:'fixed', width:'100%'}}>
                      <colgroup>
                        <col style={{width:170}}/>
                        {monthKeys.map(k => <col key={k} style={{width:80}}/>)}
                        <col style={{width:90}}/>
                      </colgroup>
                      <tbody>
                        <tr className="bg-[#0F1017]">
                          <td style={{position:'sticky', left:0, zIndex:5, background:'#1B3A6B'}}
                            className="px-3 py-2.5 text-xs font-bold text-slate-400 uppercase">Grand Total</td>
                          {monthKeys.map(k => (
                            <td key={k} className="px-2 py-2.5 text-right font-bold font-mono text-blue-400">
                              {monthTotals[k]>0 ? fmtN(monthTotals[k]) : '—'}
                            </td>
                          ))}
                          <td style={{position:'sticky', right:0, zIndex:5, background:'#0d2a1e'}}
                            className="px-3 py-2.5 text-right font-bold font-mono text-[#2FA866]">{fmtN(grandMonthTotal)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <style>{`
                  .monthly-scroll-header, .monthly-scroll-footer { overflow-x: hidden !important; }
                  .monthly-scroll-body::-webkit-scrollbar { height: 6px; }
                  .monthly-scroll-body::-webkit-scrollbar-track { background: #F3F4F6; }
                  .monthly-scroll-body::-webkit-scrollbar-thumb { background: #D1D5DB; border-radius: 3px; }
                `}</style>
              </div>
            )}
          </div>
        )
      )}
    </div>
  )
}


// ─── Main Component ────────────────────────────────────────────────────────────
export default function IndirectBudget({ initProject }) {
  const [projects, setProjects]               = useState([])
  const [selectedProject, setSelectedProject] = useState(initProject || null)
  const { can } = useProjectAuth(selectedProject?.id)
  const [activeTab, setActiveTab]             = useState('budget')
  const [toast, setToast]                     = useState(null)
  const [costCodes, setCostCodes]             = useState([])

  const [tenderItems, setTenderItems]                   = useState([])
  const [tenderLoading, setTenderLoading]               = useState(false)
  const [tenderUploadPreview, setTenderUploadPreview]   = useState(null)
  const [tenderUploading, setTenderUploading]           = useState(false)
  const [tenderUploadMode, setTenderUploadMode]         = useState(null)
  const [tenderSelected, setTenderSelected]             = useState(new Set())
  const [tenderEditItem, setTenderEditItem]             = useState(null)
  const [tenderEditForm, setTenderEditForm]             = useState({})
  const [tenderViewMode, setTenderViewMode]             = useState('table')

  const [indirectEv, setIndirectEv]           = useState({})
  const [indirectEvModal, setIndirectEvModal] = useState(null)
  const [indirectEvLocal, setIndirectEvLocal] = useState({})

  const showToast = (msg, type='success') => { setToast({ message:msg, type }); setTimeout(() => setToast(null), 4000) }

  useEffect(() => { if (initProject) setSelectedProject(initProject) }, [initProject?.id]) // eslint-disable-line

  useEffect(() => {
    const _p = getCached('projects_full')
    if (_p) setProjects(_p)
    else supabase.from('projects').select('id, name, name_ar, currency, contract_value, organizations(name)').order('name')
      .then(({ data }) => { setProjects(data || []); if (data) setCached('projects_full', data) })
  }, [])

  useEffect(() => {
    supabase.from('cost_codes').select('id, code, description, level, parent_id').eq('project_id', selectedProject?.id).eq('is_active', true).order('code')
    .eq('project_id', selectedProject?.id)
      .then(({ data }) => { if (data) setCostCodes(data) })
  }, [])

  useEffect(() => {
    if (selectedProject?.id) {
      loadTenderItems(selectedProject.id)
      loadIndirectEv(selectedProject.id)
    }
  }, [selectedProject?.id]) // eslint-disable-line

  async function loadTenderItems(pid) {
    setTenderLoading(true)
    const { data } = await supabase.from('tender_indirect_items').select('*').eq('project_id', pid).order('sort_order')
    setTenderItems(data || [])
    setTenderLoading(false)
  }

  async function loadIndirectEv(pid) {
    const { data } = await supabase.from('indirect_budget_ev').select('*').eq('project_id', pid)
    if (data) {
      const map = {}
      data.forEach(r => { map[r.cost_code] = { ev_method:r.ev_method, ev_progress:r.ev_progress, ev_qty:r.ev_qty, ev_rate:r.ev_rate, ev_manual:r.ev_manual, ev_amount:r.ev_amount } })
      setIndirectEv(map)
    }
  }

  async function saveIndirectEv(cost_code, updates) {
    const payload = { project_id: selectedProject.id, cost_code, ...updates, updated_at: new Date().toISOString() }
    await supabase.from('indirect_budget_ev').upsert(payload, { onConflict: 'project_id,cost_code' })
    setIndirectEv(prev => ({ ...prev, [cost_code]: { ...(prev[cost_code]||{}), ...updates } }))
  }

  async function parseTenderIndirectExcel(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = e => {
        try {
          const wb  = XLSX.read(e.target.result, { type:'array', cellFormula:false, cellDates:false })
          const ws  = wb.Sheets[wb.SheetNames[0]]
          const raw = XLSX.utils.sheet_to_json(ws, { header:1, defval:'', raw:true })

          // Find header row
          let headerIdx = 0
          for (let i = 0; i < Math.min(raw.length, 8); i++) {
            const joined = raw[i].join('|').toLowerCase()
            if (joined.includes('description') && joined.includes('package')) { headerIdx = i; break }
          }

          // Month row is ONE ROW BELOW header (row 3 in Excel = headerIdx + 1)
          const monthRow  = raw[headerIdx + 1] || []
          const headerRow = raw[headerIdx]
          const headers   = headerRow.map(h => String(h).trim().toLowerCase()
            .replace(/[\s\/\(\)]+/g,'_').replace(/_+/g,'_').replace(/_sar_?$/,'').replace(/_$/,''))

          // Find month cols — datetime objects parsed by XLSX as numbers > 40000
          // Also handle already-parsed Date objects
          const monthCols = []
          monthRow.forEach((v, idx) => {
            if (v instanceof Date) {
              const key = `${v.getFullYear()}-${String(v.getMonth()+1).padStart(2,'0')}`
              monthCols.push({ idx, key })
            } else if (typeof v === 'number' && v > 40000) {
              const date = new Date(Date.UTC(1899, 11, 30) + v * 86400000)
              const key  = `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}`
              monthCols.push({ idx, key })
            }
          })

          // Data starts 2 rows after header (skip the month row)
          let sort = 0
          const normalized = raw.slice(headerIdx + 2).map(rowArr => {
            const row = {}
            headers.forEach((h, i) => { row[h] = rowArr[i] ?? '' })

            const desc  = String(row['description'] || '').trim()
            if (!desc) return null

            const ptype = String(row['package_activity'] || row['package'] || '').trim().toLowerCase()
            if (ptype !== 'package' && ptype !== 'activity') return null

            const isHeader     = ptype === 'package'
            const budget       = parseFloat(row['tender_budget'] || 0) || 0
            // Qty and total_months represent the exact same concept for
            // Indirect Activities (months covered). New imports carry an
            // explicit `qty` column; when present it becomes both qty
            // and total_months so existing consumers (PR, Budget tab)
            // that already sum total_months keep working unchanged.
            // Legacy files with no qty column fall back to the existing
            // total_months column exactly as before.
            const qtyVal       = row['qty'] !== undefined && row['qty'] !== '' ? parseFloat(row['qty']) : null
            const totalM       = qtyVal != null ? qtyVal : (parseFloat(row['total_months'] || 0) || 0)
            const unitVal      = String(row['unit'] || '').trim() || null
            const rateVal      = row['rate'] !== undefined && row['rate'] !== '' ? parseFloat(row['rate']) : null
            const monthly_data = {}
            if (isHeader) {
              monthCols.forEach(({ idx, key }) => {
                const v = parseFloat(rowArr[idx]) || 0
                if (v) monthly_data[key] = v
              })
            }

            return {
              project_id:        selectedProject.id,
              item_number:       String(rowArr[0] || '').trim() || null,
              description:       desc,
              package_activity:  ptype,
              cost_code:         String(row['cost_code'] || '').trim() || null,
              department:        String(row['department___category'] || row['department'] || '').trim() || null,
              tender_budget:     budget,
              total_months:      totalM,
              qty:               qtyVal,
              unit:              unitVal,
              rate:              rateVal,
              monthly_data:      Object.keys(monthly_data).length ? monthly_data : {},
              is_package_header: isHeader,
              sort_order:        sort++,
            }
          }).filter(Boolean)

          resolve(normalized)
        } catch(err) { reject(err) }
      }
      reader.onerror = reject
      reader.readAsArrayBuffer(file)
    })
  }

  async function confirmTenderUpload(mode) {
    if (!tenderUploadPreview?.length) return
    if (tenderUploading) return
    setTenderUploadMode(null)
    setTenderUploading(true)
    if (mode === 'replace') await supabase.from('tender_indirect_items').delete().eq('project_id', selectedProject.id)
    const { error } = await supabase.from('tender_indirect_items').insert(tenderUploadPreview)
    setTenderUploading(false)
    if (error) showToast('فشل: ' + error.message, 'error')
    else { showToast(`تم استيراد ${tenderUploadPreview.length} بند`, 'success'); setTenderUploadPreview(null); loadTenderItems(selectedProject.id) }
  }

  async function deleteTenderSelected() {
    if (!tenderSelected.size || !window.confirm(`حذف ${tenderSelected.size} بند؟`)) return
    await supabase.from('tender_indirect_items').delete().in('id', [...tenderSelected])
    setTenderSelected(new Set())
    loadTenderItems(selectedProject.id)
  }

  async function saveTenderEdit() {
    if (!tenderEditItem) return
    const { error } = await supabase.from('tender_indirect_items').update({
      description:   tenderEditForm.description,
      cost_code:     tenderEditForm.cost_code  || null,
      department:    tenderEditForm.department  || null,
      tender_budget: parseFloat(tenderEditForm.tender_budget) || 0,
      total_months:  parseFloat(tenderEditForm.total_months)  || 0,
    }).eq('id', tenderEditItem.id)
    if (error) showToast('فشل: ' + error.message, 'error')
    else { showToast('تم التحديث', 'success'); setTenderEditItem(null); loadTenderItems(selectedProject.id) }
  }

  // ── Indirect Budget auto from Tender ────────────────────────────────────────
  const indirectBudgetRows = useMemo(() => {
    const activities = tenderItems.filter(i => !i.is_package_header)
    const grouped    = {}
    for (const item of activities) {
      const cc = item.cost_code || '__none__'
      if (!grouped[cc]) grouped[cc] = { cost_code:item.cost_code, department:item.department, items:[], tender_budget:0, total_months:0, monthly_data:{}, min_sort:item.sort_order??9999 }
      grouped[cc].items.push(item)
      grouped[cc].tender_budget += parseFloat(item.tender_budget) || 0
      grouped[cc].total_months  += parseFloat(item.total_months)  || 0
      if (item.monthly_data) Object.entries(item.monthly_data).forEach(([k,v]) => { grouped[cc].monthly_data[k] = (grouped[cc].monthly_data[k]||0)+v })
      if ((item.sort_order??9999) < grouped[cc].min_sort) grouped[cc].min_sort = item.sort_order??9999
    }
    return Object.values(grouped).sort((a,b) => a.min_sort - b.min_sort)
  }, [tenderItems])

  const indirectTotalBudget = indirectBudgetRows.reduce((s,r) => s + r.tender_budget, 0)

  const calcIndirectEv = useCallback((row) => {
    const ev     = indirectEv[row.cost_code] || {}
    const method = ev.ev_method || 'progress'
    const budget = parseFloat(row.tender_budget) || 0
    if (method==='progress'  && ev.ev_progress != null) return budget*(parseFloat(ev.ev_progress)/100)
    if (method==='qty_rate'  && ev.ev_qty != null && ev.ev_rate != null) return parseFloat(ev.ev_qty)*parseFloat(ev.ev_rate)
    if (method==='manual'    && ev.ev_manual != null) return parseFloat(ev.ev_manual)
    return 0
  }, [indirectEv])

  const totalIndirectEV    = indirectBudgetRows.reduce((s,r) => s + calcIndirectEv(r), 0)
  const totalIndirectEVPct = indirectTotalBudget > 0 ? totalIndirectEV/indirectTotalBudget*100 : 0

  const indirectMonthKeys = useMemo(() => {
    const keys = new Set()
    indirectBudgetRows.forEach(r => Object.keys(r.monthly_data).forEach(k => keys.add(k)))
    return Array.from(keys).sort()
  }, [indirectBudgetRows])

  return (
    <div style={{ minHeight:'100vh', background:'#F3F4F6', fontFamily:'inherit' }}>

      {/* Header */}
      <div style={{ background:'#fff', borderBottom:'1px solid #E5E7EB', padding:'18px 24px 14px' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:16 }}>
          <div>
            <h1 className="text-xl font-extrabold text-[#0F1017] tracking-tight">
              {activeTab === 'budget' ? 'Indirect Budget' : 'Tender Indirect'}
            </h1>
            <p className="text-xs text-[#6B7280] mt-0.5">
              {activeTab === 'budget' ? 'ميزانية التكاليف غير المباشرة — من Tender' : 'تحليل الـ Tender Indirect Cost'}
            </p>
          </div>
          <div className="flex items-center gap-1 bg-[#F3F4F6] border border-[#E5E7EB] rounded-xl p-1">
            {[{id:'budget',label:'📋 Budget (Indirect)'},{id:'tender',label:'📊 Tender Indirect'}].map(t => (
              <button key={t.id} onClick={() => setActiveTab(t.id)}
                className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${activeTab===t.id ? 'bg-[#0F1017] text-white' : 'text-[#6B7280] hover:bg-[#FAFAFA]'}`}>
                {t.label}
              </button>
            ))}
          </div>
          {selectedProject && (
            <div className="flex items-center gap-2 text-sm">
              <span className="font-semibold text-[#2E2C29]">{selectedProject.name}</span>
              <button onClick={() => setSelectedProject(null)} className="text-xs text-blue-500 hover:underline">تغيير</button>
            </div>
          )}
        </div>
      </div>

      <div style={{ padding:'20px 24px', display:'flex', flexDirection:'column', gap:16 }}>

        {/* Replace/Append Modal */}
        {tenderUploadMode === 'asking' && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm">
              <h3 className="font-bold text-[#0F1017] text-base mb-2">Upload {tenderUploadPreview?.length} rows</h3>
              <p className="text-sm text-[#6B7280] mb-5">Replace or append?</p>
              <div className="flex flex-col gap-2">
                {can('Budget.Edit') && (
                  <button onClick={() => confirmTenderUpload('replace')}
                    className="w-full px-4 py-2.5 bg-red-600 text-white text-sm font-medium rounded-xl hover:bg-red-700">
                    🔄 Replace — Delete existing & upload new
                  </button>
                )}
                {can('Budget.Edit') && (
                  <button onClick={() => confirmTenderUpload('append')}
                    className="w-full px-4 py-2.5 bg-[#1A7A4A] text-white text-sm font-medium rounded-xl hover:bg-[#15693f]">
                    ➕ Append — Add to existing data
                  </button>
                )}
                <button onClick={() => setTenderUploadMode(null)}
                  className="w-full px-4 py-2 text-sm text-[#6B7280] border border-[#E5E7EB] rounded-xl hover:bg-[#FAFAFA]">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Project selector */}
        {!selectedProject && (
          <div className="bg-white rounded-xl border border-[#E5E7EB] p-5">
            <label className="block text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-3">اختر المشروع</label>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {projects.map(p => (
                <button key={p.id} onClick={() => setSelectedProject(p)}
                  className="text-left px-4 py-3 rounded-xl border border-[#E5E7EB] hover:border-[#1A7A4A]/50 hover:bg-[#FAFAFA] transition-all">
                  <p className="text-sm font-semibold text-[#0F1017]">{p.name}</p>
                  <p className="text-xs text-[#9CA3AF]">{p.organizations?.name}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Tender Tab */}
        {activeTab === 'tender' && selectedProject && (
          <TenderIndirectTab
            items={tenderItems}
            loading={tenderLoading}
            uploadPreview={tenderUploadPreview}
            uploading={tenderUploading}
            onFileSelect={async (file) => { const rows = await parseTenderIndirectExcel(file); setTenderUploadPreview(rows) }}
            onConfirmUpload={() => setTenderUploadMode('asking')}
            onCancelUpload={() => { setTenderUploadPreview(null); setTenderUploadMode(null) }}
            selected={tenderSelected}
            onToggle={(id) => setTenderSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n })}
            onToggleAll={() => setTenderSelected(s =>
              s.size === tenderItems.filter(i=>!i.is_package_header).length
                ? new Set()
                : new Set(tenderItems.filter(i=>!i.is_package_header).map(i=>i.id))
            )}
            onDeleteSelected={deleteTenderSelected}
            viewMode={tenderViewMode}
            onToggleView={() => setTenderViewMode(v => v==='table'?'packages':'table')}
            editItem={tenderEditItem}
            editForm={tenderEditForm}
            onEdit={(item) => { setTenderEditItem(item); setTenderEditForm({...item}) }}
            onEditChange={(key, val) => setTenderEditForm(f => ({...f, [key]:val}))}
            onEditSave={saveTenderEdit}
            onEditCancel={() => setTenderEditItem(null)}
            onSetUploadMode={(mode) => {
              if (mode === 'cancel') { setTenderUploadPreview(null); setTenderUploadMode(null) }
              else setTenderUploadMode(mode)
            }}
            can={can}
          />
        )}

        {/* Budget Tab */}
        {activeTab === 'budget' && selectedProject && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="bg-white rounded-xl border border-[#E5E7EB] p-4">
                <div className="text-xl font-extrabold text-[#0F1017] tracking-tight">{indirectBudgetRows.length}</div>
                <div className="text-xs text-[#6B7280] mt-1">Cost Codes</div>
              </div>
              <div className="bg-[#0F1017] rounded-xl p-4">
                <div className="text-lg font-bold text-white">{fmt(indirectTotalBudget)}</div>
                <div className="text-xs text-[#9CA3AF] mt-1">إجمالي الميزانية غير المباشرة</div>
              </div>
              <div className="bg-[#1A7A4A] rounded-xl p-4">
                <div className="text-xs font-semibold text-white/60 uppercase tracking-wide mb-1">Total Earned Value</div>
                <div className="text-lg font-bold text-white">{fmt(totalIndirectEV)}</div>
                <div className="flex items-center gap-2 mt-2">
                  <div className="flex-1 h-1.5 bg-white/20 rounded-full overflow-hidden">
                    <div className="h-full bg-white rounded-full" style={{width:`${Math.min(totalIndirectEVPct,100)}%`}}/>
                  </div>
                  <span className="text-xs font-bold text-white">{totalIndirectEVPct.toFixed(1)}%</span>
                </div>
                <div className="text-xs text-white/50 mt-1">of Indirect Budget</div>
              </div>
            </div>

            {tenderLoading ? (
              <div className="text-center py-16 text-[#9CA3AF] text-sm">Loading...</div>
            ) : indirectBudgetRows.length === 0 ? (
              <div className="text-center py-16 text-[#9CA3AF] text-sm">لا يوجد بيانات — ارفع Excel في تاب Tender أولاً</div>
            ) : (
              <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#F9FAFB] border-b border-[#E5E7EB]">
                      <tr>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-[#6B7280] uppercase">Cost Code</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-[#6B7280] uppercase">Department</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-[#6B7280] uppercase">Total Budget</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-[#6B7280] uppercase">Total Months</th>
                        {indirectMonthKeys.slice(0,4).map(k => (
                          <th key={k} className="text-right px-3 py-3 text-xs font-semibold text-blue-600 uppercase whitespace-nowrap">
                            {new Date(k+'-01').toLocaleDateString('en-US',{month:'short',year:'2-digit'})}
                          </th>
                        ))}
                        {indirectMonthKeys.length > 4 && <th className="px-3 py-3 text-xs text-[#9CA3AF]">+{indirectMonthKeys.length-4}</th>}
                        <th className="text-center px-4 py-3 text-xs font-semibold text-purple-600 uppercase">Progress</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-[#1A7A4A] uppercase">Earned Value</th>
                        <th className="px-4 py-3"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#F3F4F6]">
                      {indirectBudgetRows.map((row, idx) => {
                        const ccObj = costCodes.find(c => c.code?.trim() === row.cost_code?.trim())
                        const ev    = indirectEv[row.cost_code] || {}
                        const evAmt = calcIndirectEv(row)
                        return (
                          <tr key={idx} className="hover:bg-[#FAFAFA]">
                            <td className="px-4 py-3 font-mono text-xs text-[#1A7A4A] font-semibold">{row.cost_code || '—'}</td>
                            <td className="px-4 py-3 text-sm text-[#0F1017]">{ccObj?.description || row.department || '—'}</td>
                            <td className="px-4 py-3 text-right font-bold font-mono text-xs text-[#0F1017]">{fmtN(row.tender_budget)}</td>
                            <td className="px-4 py-3 text-right font-mono text-xs text-[#6B7280]">{fmtN(row.total_months)}</td>
                            {indirectMonthKeys.slice(0,4).map(k => (
                              <td key={k} className="px-3 py-3 text-right font-mono text-xs text-blue-600">
                                {row.monthly_data[k] ? fmtN(row.monthly_data[k]) : '—'}
                              </td>
                            ))}
                            {indirectMonthKeys.length > 4 && <td />}
                            <td className="px-4 py-3 text-center text-xs text-[#6B7280]">
                              {ev.ev_method==='progress' ? `${ev.ev_progress??'—'}%`
                               : ev.ev_method==='qty_rate' ? `${ev.ev_qty??'—'} × ${ev.ev_rate??'—'}`
                               : ev.ev_method==='manual' ? 'Manual' : '—'}
                            </td>
                            <td className="px-4 py-3 text-right font-bold font-mono text-xs text-[#1A7A4A]">{evAmt>0 ? fmtN(evAmt) : '—'}</td>
                            <td className="px-4 py-3 text-center">
                              <button onClick={() => { setIndirectEvModal(row); setIndirectEvLocal(ev) }}
                                className="px-2 py-1 text-xs border border-purple-300 text-purple-700 rounded hover:bg-purple-50">
                                {ev.ev_method ? '✏️ Edit EV' : '+ EV'}
                              </button>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                    <tfoot className="bg-[#0F1017]">
                      <tr>
                        <td colSpan={2} className="px-4 py-3 text-xs font-bold text-slate-400 uppercase">Grand Total</td>
                        <td className="px-4 py-3 text-right text-sm font-bold text-white font-mono">{fmtN(indirectTotalBudget)}</td>
                        <td className="px-4 py-3 text-right text-sm font-bold text-blue-400 font-mono">
                          {fmtN(indirectBudgetRows.reduce((s,r)=>s+r.total_months,0))}
                        </td>
                        {indirectMonthKeys.slice(0,4).map(k=>(
                          <td key={k} className="px-3 py-3 text-right text-xs font-bold text-blue-400 font-mono">
                            {fmtN(indirectBudgetRows.reduce((s,r)=>s+(r.monthly_data[k]||0),0))}
                          </td>
                        ))}
                        {indirectMonthKeys.length > 4 && <td />}
                        <td />
                        <td className="px-4 py-3 text-right text-sm font-bold text-[#2FA866] font-mono">{fmtN(totalIndirectEV)}</td>
                        <td />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}

      {/* EV Modal */}
      {indirectEvModal && (() => {
        const row    = indirectEvModal
        const local  = indirectEvLocal
        const method = local.ev_method || 'progress'
        const budget = parseFloat(row.tender_budget) || 0
        const previewEV = () => {
          if (method==='progress' && local.ev_progress!=null && local.ev_progress!=='') return budget*(parseFloat(local.ev_progress)/100)
          if (method==='qty_rate' && local.ev_qty!=null && local.ev_rate!=null) return parseFloat(local.ev_qty)*parseFloat(local.ev_rate)
          if (method==='manual' && local.ev_manual!=null && local.ev_manual!=='') return parseFloat(local.ev_manual)
          return null
        }
        const ev = previewEV()
        return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-[#F3F4F6] rounded-2xl shadow-2xl w-full max-w-md">
              <div className="flex items-center justify-between p-5 border-b border-[#E5E7EB]">
                <div>
                  <h2 className="text-base font-bold text-[#0F1017]">Earned Value</h2>
                  <p className="text-xs text-[#9CA3AF] mt-0.5">{row.cost_code} — {row.department}</p>
                </div>
                <button onClick={() => setIndirectEvModal(null)} className="text-[#9CA3AF] hover:text-[#6B7280] text-xl">×</button>
              </div>
              <div className="p-5 space-y-4">
                <div className="bg-white rounded-xl px-4 py-3 flex items-center justify-between">
                  <span className="text-xs text-[#9CA3AF] uppercase tracking-wide">Budget</span>
                  <span className="font-bold text-[#1A7A4A]">{fmtN(budget)}</span>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-2">Method</label>
                  <div className="grid grid-cols-3 gap-2">
                    {[{v:'progress',l:'Progress %'},{v:'qty_rate',l:'Qty × Rate'},{v:'manual',l:'Manual'}].map(m => (
                      <button key={m.v} onClick={() => setIndirectEvLocal(p => ({...p, ev_method:m.v}))}
                        className={`py-2 rounded-lg text-xs font-medium border transition-colors ${method===m.v ? 'bg-purple-600 text-white border-purple-600' : 'border-[#E5E7EB] text-[#6B7280] hover:bg-[#FAFAFA]'}`}>
                        {m.l}
                      </button>
                    ))}
                  </div>
                </div>
                {method==='progress' && (
                  <div>
                    <label className="block text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-1">Progress %</label>
                    <input type="number" min="0" max="100" step="0.5" placeholder="0 – 100" value={local.ev_progress??''}
                      onChange={e => setIndirectEvLocal(p => ({...p, ev_progress:e.target.value}))}
                      className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"/>
                  </div>
                )}
                {method==='qty_rate' && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-1">Qty</label>
                      <input type="number" placeholder="Quantity" value={local.ev_qty??''}
                        onChange={e => setIndirectEvLocal(p => ({...p, ev_qty:e.target.value}))}
                        className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"/>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-1">Rate</label>
                      <input type="number" placeholder="Rate" value={local.ev_rate??''}
                        onChange={e => setIndirectEvLocal(p => ({...p, ev_rate:e.target.value}))}
                        className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"/>
                    </div>
                  </div>
                )}
                {method==='manual' && (
                  <div>
                    <label className="block text-xs font-semibold text-[#6B7280] uppercase tracking-wide mb-1">EV Amount</label>
                    <input type="number" placeholder="Enter EV directly" value={local.ev_manual??''}
                      onChange={e => setIndirectEvLocal(p => ({...p, ev_manual:e.target.value}))}
                      className="w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"/>
                  </div>
                )}
                {ev != null && (
                  <div className="bg-purple-50 border border-purple-200 rounded-xl px-4 py-3 flex items-center justify-between">
                    <span className="text-xs text-purple-600 font-semibold uppercase tracking-wide">EV Preview</span>
                    <span className="text-lg font-black text-purple-700">{fmtN(ev)}</span>
                  </div>
                )}
              </div>
              <div className="flex gap-3 p-5 border-t border-[#E5E7EB]">
                <button onClick={() => setIndirectEvModal(null)}
                  className="flex-1 py-2 border border-[#E5E7EB] rounded-lg text-sm hover:bg-[#FAFAFA]">Cancel</button>
                {can('Budget.Edit') && (
                  <button onClick={async () => {
                  const evMethod = local.ev_method || 'progress'
                  const evProgress = local.ev_progress != null ? parseFloat(local.ev_progress) : null
                  const evQty = local.ev_qty != null ? parseFloat(local.ev_qty) : null
                  const evRate = local.ev_rate != null ? parseFloat(local.ev_rate) : null
                  const evManual = local.ev_manual != null ? parseFloat(local.ev_manual) : null
                  // Persist the computed EV amount (in currency) alongside the raw inputs —
                  // same formula as previewEV above, computed once at save time so downstream
                  // consumers (Cost Report shared service) can read a ready number instead of
                  // re-deriving it from method/progress/qty/rate on every read.
                  let evAmount = 0
                  if (evMethod === 'progress' && evProgress != null) evAmount = budget * (evProgress / 100)
                  else if (evMethod === 'qty_rate' && evQty != null && evRate != null) evAmount = evQty * evRate
                  else if (evMethod === 'manual' && evManual != null) evAmount = evManual
                  await saveIndirectEv(row.cost_code, {
                    ev_method:   evMethod,
                    ev_progress: evProgress,
                    ev_qty:      evQty,
                    ev_rate:     evRate,
                    ev_manual:   evManual,
                    ev_amount:   evAmount,
                  })
                  setIndirectEvModal(null)
                }} className="flex-1 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700">
                  Save EV
                </button>
                )}
              </div>
            </div>
          </div>
        )
      })()}
    </div>
  )
}
